import React, {useState} from 'react';
import {Box, Center, KeyboardAvoidingView} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {
  useForm,
  SubmitHandler,
  SubmitErrorHandler,
  FieldErrors,
} from 'react-hook-form';
import ExternalTemplate from '../../components/templates/external';
import {PrimaryButton} from '../../components/buttons';
import {PasswordInput} from '../../components/inputs/password';
import {useContextState as userContext} from '../../context/user';
import {StackParamList} from '../../navigators';
import {IRecoverForm} from '../../interfaces/forms';
import useToast from '../../hooks';
import {ResultState} from '../../constants/enumerations';
import {SpinnerModal} from '../../components/modals';
import {message} from '../../utils/messages';

type ScreenProps = NativeStackScreenProps<StackParamList, 'PasswordChange'>;

export const PasswordChangeScreen = ({navigation}: ScreenProps) => {
  const {
    control,
    handleSubmit,
    watch,
    formState: {errors},
  } = useForm({
    defaultValues: {
      password: '',
      confirm: '',
    } as IRecoverForm,
  });
  const {showServerError, showInputsError} = useToast();
  const [loading, setLoading] = useState(false);
  const {changePassword} = userContext();

  const onSubmit: SubmitHandler<IRecoverForm> = async (data: IRecoverForm) => {
    setLoading(true);
    const result = await changePassword(data.password);

    if (result === ResultState.success) {
      setLoading(false);
    } else {
      if (result === ResultState.rejected) {
        setLoading(false);
        showInputsError();
      } else {
        setLoading(false);
        showServerError();
      }
    }
  };

  const onInvalid: SubmitErrorHandler<IRecoverForm> = async (
    fieldErrors: FieldErrors<IRecoverForm>,
  ) => {
    if (fieldErrors) {
      showInputsError();
    }
  };

  const handleGoBack = () => {
    navigation.navigate('SignIn');
  };

  return (
    <ExternalTemplate
      title="password-change-title"
      summary="password-change-summary"
      backButton={{
        onPress: () => handleGoBack(),
      }}>
      <KeyboardAvoidingView behavior="height" flex="1" enabled={true}>
        <SpinnerModal show={loading} title={message('spinner-sending')} />
        <Center width="100%" flex={1} justifyContent="flex-start">
          <Box width="100%">
            <PasswordInput
              control={control}
              name="password"
              label="password-change-new-password"
              error={errors.password}
              rules={{
                required: 'form-login-is-required',
              }}
            />
            <PasswordInput
              control={control}
              name="confirm"
              label="password-change-label-confirm"
              error={errors.confirm}
              rules={{
                required: 'form-login-is-required',
                validate: (val: string) => {
                  if (watch('password') !== val) {
                    return 'password-change-no-match';
                  }
                },
              }}
            />
          </Box>
          <Box width="100%" position="absolute" bottom="0" safeArea mb="5">
            <PrimaryButton
              label="password-change-button"
              onPress={handleSubmit(onSubmit, onInvalid)}
            />
          </Box>
        </Center>
      </KeyboardAvoidingView>
    </ExternalTemplate>
  );
};
